import React, { useState, useEffect } from 'react';

// Hooks
import { useSelector } from 'react-redux';
import { displayTooltip } from '~/hooks/displayTooltip';

//Utils
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';
import { stringLength } from '~/utils/helperFunctions';

// Components
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import OlcBulkSelectionDropdown from '~/components/General/OlcBulkSelectionDropdown';
import ImpersonateModal from '~/components/AdminPortal/Users/ImpersonationModal';

// MUI Components
import DataGrid from '~/components/General/OlcDataGrid';
import {
  Box,
  MenuItem,
  Typography,
  Tooltip,
  Stack,
  IconButton,
  Menu,
  Checkbox,
  useMediaQuery,
} from '@mui/material';

// Icons
import {
  UserCIcon,
  DisableCIcon,
  CheckmarkCIcon,
  NoContactCIcon,
  TransactionCIcon,
  RemoveFundsCIcon,
  AddFundsCIcon,
  CopyIconCIcon,
} from '~/assets/images';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DoneIcon from '@mui/icons-material/Done';
import RemoveIcon from '@mui/icons-material/Remove';

// Styles
import './styles.scss';

const UsersTable = ({
  pagination,
  updatePagination,
  setOpenActionModal,
  selectedUser,
  setSelectedUser,
  setOpenFundsModal,
  searchApplied,
  setSelectedRows,
  selectedRows,
  setOpenRemoveFundsModal,
  handleViewTransactions,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [actions, setActions] = useState(null);
  const [activeRowId, setActiveRowId] = useState(null);
  const [openImpersonationModal, setImpersonationModal] = useState(false);
  const [bulkSelect, setBulkSelect] = useState(false);
  const [removeBulkSelect, setRemoveBulkSelect] = useState(false);
  const [rows, setRows] = useState([]);

  const open = Boolean(anchorEl);
  const openActions = Boolean(actions);

  const pageSizes = DEFAULT_PAGE_SIZES;

  const fetchedRows = useSelector((state) => state.adminUsersReducers.users);
  const rowsCount = useSelector((state) => state.adminUsersReducers.rowsCount);
  const to = useSelector((state) => state.adminUsersReducers.to);
  const from = useSelector((state) => state.adminUsersReducers.from);
  const lastPage = useSelector((state) => state.adminUsersReducers.lastPage || 1);
  const currentPage = useSelector((state) => state.adminUsersReducers.currentPage || 1);
  const perPage = useSelector(
    (state) => state.adminUsersReducers.perPage || DEFAULT_PAGINATION.pageSize
  );

  const isLoading = useSelector((state) => state.adminUsersReducers.loading);

  const isTooltip = displayTooltip(4000);
  const isTooltipPhone = displayTooltip(2100);
  const shouldFitColumnsToDesktop = useMediaQuery('(min-width: 1600px)');

  const handleChangePage = (event, newPage) => {
    updatePagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const selectAll = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((contact) => ({ ...contact, checked: true }));
    setRows(updatedRows);
    setSelectedRows([-1]);
  };

  const selectVisble = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((contact) => ({ ...contact, checked: true }));
    setRows(updatedRows);

    const selectedRowIds = updatedRows.map((contact) => contact.id);
    setSelectedRows(selectedRowIds);
  };

  const selectNone = () => {
    setBulkSelect(false);
    setRemoveBulkSelect(false);
    const updatedRows = rows.map((contact) => ({ ...contact, checked: false }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  const selectRow = (event, selectedRow) => {
    event.stopPropagation();

    // Update the selectedRow object directly in the rows array
    const updatedRows = rows.map((row) =>
      row.id === selectedRow.id ? { ...row, checked: !row.checked } : row
    );

    // If the user selects all contacts and then removes a single contact, update the selected rows array accordingly.
    if (selectedRows[0] === -1) {
      const filteredRows = rows.map((row) =>
        row.id === selectedRow.id ? { ...row, checked: !row.checked } : { ...row, checked: true }
      );

      const filteredRowsIds = filteredRows
        .map((contact) => contact.checked === true && contact.id)
        .filter(Boolean);

      setSelectedRows(filteredRowsIds);
      setRows(updatedRows);
      setRemoveBulkSelect(true);
      return;
    }

    setRows(updatedRows);

    const selectedRowIds = selectedRows.includes(selectedRow.id)
      ? selectedRows.filter((id) => id !== selectedRow.id) // Remove the ID if already selected
      : [...selectedRows, selectedRow.id]; // Add the ID if not selected

    // Update the selectedRows state with the new array
    setSelectedRows(selectedRowIds);

    setRemoveBulkSelect(true);
  };

  const bulkSelection = () => {
    const newBulkSelect = !bulkSelect;
    setBulkSelect(newBulkSelect);

    const updatedRows = rows.map((contact) => ({
      ...contact,
      checked: newBulkSelect,
    }));
    setRows(updatedRows);

    const selectedRowIds = updatedRows.map((contact) => contact.id);
    newBulkSelect ? setSelectedRows(selectedRowIds) : setSelectedRows([]);
  };

  const removeBulkSelection = () => {
    const newBulkSelect = !removeBulkSelect;
    setRemoveBulkSelect(newBulkSelect);
    setBulkSelect(newBulkSelect);

    const updatedRows = rows.map((contact) => ({
      ...contact,
      checked: newBulkSelect,
    }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [currentPage, perPage]);

  useEffect(() => {
    !selectedRows.length && setRemoveBulkSelect(false);
    (rows.length && selectedRows.length === rows.length) || selectedRows[0] === -1
      ? (setBulkSelect(true), setRemoveBulkSelect(false))
      : setBulkSelect(false);
  }, [selectedRows, rows]);

  useEffect(() => {
    setRows(fetchedRows);
  }, [fetchedRows]);

  const accountIdTooltip = (params) => {
    console.log({ params });
    return (
      <Box className="accountTooltipText">
        <Typography>
          <span>User ID: </span>
          {params.row.id}
        </Typography>
        <Typography>
          <span>Account ID: </span>
          {params.row.orgId}
        </Typography>
      </Box>
    );
  };

  const columns = [
    {
      field: ' ',
      headerClassName: 'olcBulkSelectHeader',
      cellClassName: 'olcBulkSelectCell',
      renderHeader: () => (
        <Box className="checkboxHeader">
          {removeBulkSelect ? (
            <Checkbox
              className="bulkCheck"
              icon={<Box className="bulkNotchecked" />}
              checkedIcon={<RemoveIcon />}
              checked={removeBulkSelect}
              onClick={removeBulkSelection}
            />
          ) : (
            <Checkbox
              className="bulkCheck"
              icon={<Box className="bulkNotchecked" />}
              checkedIcon={<DoneIcon />}
              checked={bulkSelect}
              onClick={bulkSelection}
            />
          )}
          <OlcBulkSelectionDropdown
            ariaLabel="Open user selection options"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            open={open}
          />
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            MenuListProps={{
              className: 'bulkMenuOptions',
            }}
          >
            <MenuItem
              onClick={rowsCount > 0 ? selectAll : undefined}
              className={selectedRows[0] === -1 ? 'highlight' : ''}
            >
              {MESSAGES.ADMIN_DASHBOARD.USERS.SELECT_ALL_ITEMS} ({rowsCount})
            </MenuItem>
            <MenuItem onClick={selectNone}>{MESSAGES.ADMIN_DASHBOARD.USERS.SELECT_NONE}</MenuItem>
            <MenuItem onClick={selectVisble}>
              {MESSAGES.ADMIN_DASHBOARD.USERS.SELECT_VISIBLE_ITEMS}
            </MenuItem>
          </Menu>
        </Box>
      ),
      width: 75,
      sortable: false,
      renderCell: (params) => (
        <Box className="checkboxRow">
          <Checkbox
            onClick={(event) => selectRow(event, params.row)}
            className="bulkCheck"
            icon={<Box className="bulkNotchecked" />}
            checkedIcon={<DoneIcon />}
            checked={params.row.checked}
          />
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      headerClassName: 'olcBulkSelectAdjacentHeader',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={accountIdTooltip(params)}>
          <Box className="addressWrapper">
            <Typography>{params.row.id || ' -'}</Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'company',
      headerName: 'Company',
      width: 150,
      sortable: false,
      flex: window.innerWidth >= 1600 ? 1 : 0,
      renderCell: (params) =>
        stringLength(`${params.row.company}`, 15) ? (
          <Tooltip title={params.row.company}>
            <Box className="companyWrapper">
              <Typography>{params.row.company || ' -'}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="companyWrapper">
            <Typography>{params.row.company || ' -'}</Typography>
          </Box>
        ),
    },
    {
      field: 'name',
      headerName: 'Name',
      type: 'number',
      width: 150,
      sortable: false,
      flex: window.innerWidth >= 1600 ? 1 : 0,
      renderCell: (params) =>
        stringLength(`${params.row.name}`, 20) ? (
          <Tooltip title={params.row.name}>
            <Box className="userNameWrapper">
              <Typography>{params.row.name || ' -'}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="userNameWrapper">
            <Typography>{params.row.name || ' -'}</Typography>
          </Box>
        ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 170,
      height: 500,
      sortable: false,
      flex: window.innerWidth >= 1600 ? 1 : 0,
      renderCell: (params) => {
        const emailContent = <Typography>{params.row.email || ' -'}</Typography>;
        return stringLength(`${params.row.email}`, 14) && !isTooltip ? (
          <Box className="emailWrapper">
            <Tooltip title={params.row.email}>{emailContent}</Tooltip>
          </Box>
        ) : (
          <Box className="emailWrapper">{emailContent}</Box>
        );
      },
    },
    {
      field: 'phoneNo',
      headerName: 'Phone No',
      width: 150,
      sortable: false,
      flex: window.innerWidth >= 1600 ? 1 : 0,
      renderCell: (params) => {
        const phoneNoContent = (
          <Box className="emailWrapper">
            <Typography>{params.row.phoneNo || ' -'}</Typography>
          </Box>
        );
        return stringLength(`${params.row.phoneNo}`, 14) && !isTooltipPhone ? (
          <Tooltip title={params.row.phoneNo}>{phoneNoContent}</Tooltip>
        ) : (
          <Box className="emailWrapper">{phoneNoContent}</Box>
        );
      },
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.role || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'plan',
      headerName: 'Plan',
      width: 150,
      sortable: false,
      flex: window.innerWidth >= 1600 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.plan || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'orders',
      headerName: 'Orders',
      width: 70,
      sortable: false,
      headerClassName: 'ordersHeader',
      renderCell: (params) => (
        <Box className="addressWrapper centerAlignCell">
          <Typography>{params.row.orders || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'revenue',
      headerName: 'Revenue',
      width: 120,
      sortable: false,
      headerClassName: 'ordersHeader',
      renderCell: (params) => (
        <Box className="addressWrapper centerAlignCell">
          <Typography>{params.row.revenue || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'signUpDate',
      headerName: 'Signed Up',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.signUpDate || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Last Active',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.updatedAt || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'Action',
      headerName: 'Action',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <>
          <Box>
            <IconButton
              aria-label="more"
              id="long-button"
              aria-controls={
                openActions && activeRowId?.id === params.row.id ? 'long-menu' : undefined
              }
              aria-expanded={openActions && activeRowId?.id === params.row.id ? 'true' : undefined}
              aria-haspopup="true"
              onClick={(event) => {
                setActiveRowId(params.row); // Set active row ID when the menu is opened
                setActions(event.currentTarget);
              }}
            >
              <MoreVertIcon
                sx={{
                  color: activeRowId?.id === params.row.id && openActions ? '#ed5c2f' : 'inherit',
                }}
              />
            </IconButton>
            <OlcActionMenu
              id="long-menu"
              MenuListProps={{
                'aria-labelledby': 'long-button',
              }}
              anchorEl={actions}
              open={openActions && activeRowId?.id === params.row.id}
              onClose={() => setActions(null)}
            >
              {activeRowId?.status !== 'Invited' && (
                <MenuItem
                  onClick={() => {
                    setSelectedUser(activeRowId);
                    setImpersonationModal(true);
                    setActions(null);
                  }}
                >
                  <img src={UserCIcon} alt="impersonate" />
                  {MESSAGES.ADMIN_DASHBOARD.USERS.USER_ACTIONS.IMPERSONATE}
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setSelectedUser(activeRowId);
                  setOpenFundsModal(true);
                  setActions(null);
                }}
              >
                <img src={AddFundsCIcon} alt="funds" />
                {MESSAGES.ADMIN_DASHBOARD.USERS.USER_ACTIONS.ADD_FUNDS}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSelectedUser(activeRowId);
                  setOpenRemoveFundsModal(true);
                  setActions(null);
                }}
              >
                <img src={RemoveFundsCIcon} alt="remove-funds" />
                {MESSAGES.ADMIN_DASHBOARD.USERS.USER_ACTIONS.REMOVE_FUNDS}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSelectedUser(activeRowId);
                  setOpenActionModal(true);
                  setActions(null);
                }}
              >
                <img
                  src={activeRowId?.status === 'Blocked' ? CheckmarkCIcon : DisableCIcon}
                  alt="enable"
                />
                {activeRowId?.status === 'Blocked'
                  ? MESSAGES.ADMIN_DASHBOARD.USERS.USER_ACTIONS.ENABLE_ACCOUNT
                  : MESSAGES.ADMIN_DASHBOARD.USERS.USER_ACTIONS.DISABLE_ACCOUNT}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleViewTransactions(activeRowId);
                  setActions(null);
                }}
              >
                <img src={TransactionCIcon} alt="view-transactions" />
                {MESSAGES.ADMIN_DASHBOARD.USERS.USER_ACTIONS.VIEW_TRANSACTIONS}
              </MenuItem>
            </OlcActionMenu>
          </Box>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="userAdmin olcAdminListTable">
        <DataGrid
          rows={isLoading ? [] : rows}
          columns={columns}
          autoHeight={false}
          shrinkColumnsToFit={shouldFitColumnsToDesktop}
          loading={isLoading}
          rowCount={rowsCount}
          pagination={pagination}
          paginationMode="server"
          hideFooterSelectedRowCount
          hideFooterPagination
          rowSelection={false}
          getRowId={(row) => row?.key}
          disableColumnMenu={true}
          className="userAdminTable"
          columnHeaderHeight={47}
          slots={{
            noRowsOverlay: () => (
              <Stack className="noRowsTextContacts">
                <img src={NoContactCIcon} alt="NoContact" />
                {`${MESSAGES.NO_RESULTS_TEXT} ${!searchApplied ? 'filters' : 'search'}`}
              </Stack>
            ),
            noResultsOverlay: () => (
              <Stack className="noRowsTextContacts">
                <img src={NoContactCIcon} alt="NoContact" />
                {MESSAGES.NO_ROWS_TEXT}
              </Stack>
            ),
          }}
        />
        {(isLoading || rowsCount > 0) && (
          <OlcTablePagination
            count={lastPage}
            page={currentPage}
            pageSize={perPage}
            pageSizes={pageSizes}
            onPageChange={handleChangePage}
            onPageSizeChange={(event) =>
              updatePagination({ page: 0, pageSize: event.target.value })
            }
            from={from}
            to={to}
            total={rowsCount}
            loading={isLoading}
          />
        )}
      </div>
      <ImpersonateModal
        open={openImpersonationModal}
        handleClose={() => setImpersonationModal(false)}
        selectedUser={selectedUser}
      />
    </>
  );
};

export default UsersTable;
